import {
  cloneDeep,
  get,
  has,
  head,
  isEqual,
  partition,
  set,
  setWith,
  unset,
} from 'lodash';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { injectIntl, intlShape } from 'react-intl';
import { Redirect, withRouter } from 'react-router-dom';
import RouterPropTypes from 'react-router-prop-types';
import { Col, Row } from 'reactstrap';

import { modelOf } from '../../../prop-types';
import globalTranslations from '../../../i18n/globalTranslations';
import RouteService from '../../../services/RouteService';
import CategoryStore from '../../../store/CategoryStore';
import ConfigStore from '../../../store/ConfigStore';
import ManufacturerStore from '../../../store/ManufacturerStore';
import ProductStore from '../../../store/ProductStore';
import UIStore from '../../../store/UIStore';
import FilterType from '../../../types/FilterType';
import ProductClass from '../../../types/ProductClass';
import ProductSortOrderBy from '../../../types/ProductSortOrderBy';
import { scrollToElementById } from '../../../util/dom';
import { paramsToQueryIdentifier } from '../../../util/query';
import { parse, stringify } from '../../../util/queryString';
import {
  getCategoryManufacturerRouteState,
  resolveManufacturerFromParam,
} from '../../../util/categoryManufacturerRouting';
import {
  escapeUrlForPaginatorTemplate,
  escapeUrlParametersForPaginatorTemplate,
} from '../../../util/url';
import ScrollableAnchor from '../../anchor/ScrollableAnchor';
import MobilePaginator from '../../common/MobilePaginator';
import Paginator from '../../common/Paginator';
import RobotsMeta from '../../head/RobotsMeta';
import SEOPagination from '../../head/SEOPagination';
import ContentForState from '../../loader/ContentForState';
import PageSkeleton from '../../skeleton/PageSkeleton';
import ActiveFilterList from '../ActiveFilterList';
import BooleanFilter from '../BooleanFilter';
import FilteringOptions from '../FilteringOptions';
import NoProductsFound from '../NoProductsFound';
import ProductList from '../ProductList';
import ProductListFilter from '../ProductListFilter';
import ProductListTools from '../ProductListTools';

const ProductSortParameters = {
  ORDER_BY: 'orderBy',
  ORDER: 'order',
};

// set treats numeric keys as arrays, we use setWith to force objects
const setObject = (object, path, value) => {
  setWith(object, path, value, Object);
};

export const ANCHOR_ID = 'mainProductList';
const IN_STOCK_FILTER_KEY = 'inStock';
const MANUFACTURER_FILTER_KEY = 'manufacturers';

/* TODO This requires refactoring. Currently the API call is not canceled when parent is unmounted.
 *   Separate component for loading products */
@observer
export class MainProductList extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showAllFilters: false,
      searchTerm: '',
    };

    this.validateActiveSortParameters();

    if (typeof window === 'undefined' || window.isSSR) {
      const manufacturerParam = this.getManufacturerParam();
      if (manufacturerParam) {
        this.loadManufacturersAndThen(() => {
          this.loadProducts();
        });
      } else {
        this.loadProducts();
      }
    }
  }

  componentDidMount() {
    const manufacturerParam = this.getManufacturerParam();
    if (
      manufacturerParam &&
      this.props.manufacturerStore &&
      !this.props.manufacturerStore.isLoaded &&
      !this.props.manufacturerStore.isLoading
    ) {
      this.loadManufacturersAndThen(() => {
        if (this.maybeNormalizeManufacturerParam()) {
          return;
        }
        this.loadProducts();
      });
      return;
    }

    if (this.maybeNormalizeManufacturerParam()) {
      return;
    }
    this.loadProducts();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.location.search !== this.props.location.search ||
      prevProps.match.params.manufacturer !==
        this.props.match.params.manufacturer
    ) {
      const manufacturerParam = this.getManufacturerParam();
      if (
        manufacturerParam &&
        this.props.manufacturerStore &&
        !this.props.manufacturerStore.isLoaded &&
        !this.props.manufacturerStore.isLoading
      ) {
        this.loadManufacturersAndThen(() => {
          if (this.maybeNormalizeManufacturerParam()) {
            return;
          }
          this.loadProducts();
        });
        return;
      }

      if (this.maybeNormalizeManufacturerParam()) {
        return;
      }
    }

    if (
      !isEqual(prevProps.fixedParams, this.props.fixedParams) ||
      !isEqual(prevState.searchParameters, this.getSearchParameters()) ||
      prevState.searchTerm !== this.state.searchTerm
    ) {
      this.loadProducts();
    }
    this.validateFilters();
  }

  getSearchParameters = () =>
    MainProductList.propsToSearchParameters(this.props);

  loadManufacturersAndThen = (onReady) => {
    const { manufacturerStore } = this.props;
    if (!manufacturerStore) {
      onReady();
      return;
    }

    if (manufacturerStore.isLoaded || manufacturerStore.isLoading) {
      onReady();
      return;
    }

    manufacturerStore
      .loadManufacturers({ excludeContent: true })
      .then(() => {
        onReady();
      })
      .catch(() => {
        onReady();
      });
  };

  /**
   *  Check if current filters can all be found in the current search result.
   *  If not, remove them, because it means they are not longer valid.
   */
  validateFilters = () => {
    const searchParameters = this.getSearchParameters();
    const searchResult = this.getCurrentSearchResult();
    if (searchResult && searchResult.available_filters) {
      const validFilters = {};

      searchResult.available_filters.forEach((filter) => {
        const currentValue = get(searchParameters.filters, filter.key);
        if (currentValue) {
          setObject(validFilters, filter.key, currentValue);
        }
      });

      if (!isEqual(searchParameters.filters, validFilters)) {
        this.setSearchParameters({
          ...searchParameters,
          filters: validFilters,
        });
      }
    }
  };

  validateActiveSortParameters = () => {
    const { configStore, productStore } = this.props;
    const productListSelectors =
      configStore.productList.productListSortSelectors;
    const searchParameters = this.getSearchParameters();

    if (
      !productListSelectors ||
      !searchParameters.sort ||
      !Object.keys(searchParameters.sort).length
    ) {
      return;
    }

    const activeProductSortSelectors = productStore.activeProductSortList();

    const validSortParameter = activeProductSortSelectors.find(
      (ss) =>
        ss.sortKey === searchParameters.sort.orderBy &&
        ss.sortOrder === searchParameters.sort.order
    );

    if (!validSortParameter) {
      searchParameters.sort = {};
      this.setSearchParameters(searchParameters);
    }
  };

  // Manufacturer URL rules for category pages:
  // - URL path uses slug/name; API filter uses id when available.
  // - Single manufacturer with no other query params on page=1 is promoted to URL.
  hasCategoryContext = () =>
    this.props.isCategoryPage && this.props.categoryStore?.activeCategory;

  getManufacturerParam = () => this.props.match?.params?.manufacturer;

  getCategoryPath = () => {
    const { routeService, categoryStore } = this.props;
    return routeService.getCategoryPath(categoryStore.activeCategory);
  };

  normalizeCategoryPath = (path) => (path.endsWith('/') ? path : `${path}/`);

  buildCategoryPathWithManufacturer = (manufacturerValue, filter = null) => {
    if (!this.hasCategoryContext()) {
      return null;
    }
    const basePath = this.normalizeCategoryPath(this.getCategoryPath());
    const manufacturerUrlValue = manufacturerValue
      ? this.getManufacturerUrlValueFromFilter(manufacturerValue, filter)
      : null;
    return manufacturerUrlValue
      ? `${basePath}${manufacturerUrlValue}/`
      : basePath;
  };

  getManufacturerFromParam = (manufacturerParam) => {
    const { manufacturerStore } = this.props;
    if (!manufacturerParam || !manufacturerStore) {
      return null;
    }

    return resolveManufacturerFromParam(manufacturerStore, manufacturerParam);
  };

  getCategoryManufacturerRouteState = (
    searchParameters = this.getSearchParameters()
  ) =>
    getCategoryManufacturerRouteState({
      manufacturerStore: this.props.manufacturerStore,
      manufacturerParam: this.getManufacturerParam(),
      queryManufacturerValues:
        this.getManufacturerFilterValues(searchParameters),
    });

  setManufacturerFilterValues = (filters, values) => {
    const nextFilters = Array.isArray(filters)
      ? [...filters]
      : { ...(filters || {}) };

    if (values.length > 0) {
      setObject(nextFilters, MANUFACTURER_FILTER_KEY, values);
    } else {
      unset(nextFilters, MANUFACTURER_FILTER_KEY);
    }

    return nextFilters;
  };

  buildSearchParametersWithManufacturerValues = (searchParameters, values) => {
    const nextParameters = {
      ...searchParameters,
      filters: Array.isArray(searchParameters.filters)
        ? [...searchParameters.filters]
        : { ...(searchParameters.filters || {}) },
    };

    nextParameters.filters = this.setManufacturerFilterValues(
      nextParameters.filters,
      values
    );

    return nextParameters;
  };

  getManufacturerFilterValueFromParam = (manufacturerParam) => {
    const manufacturer = this.getManufacturerFromParam(manufacturerParam);
    return manufacturer ? manufacturer.id : manufacturerParam;
  };

  getManufacturerUrlValueFromFilter = (manufacturerValue, filter = null) => {
    const manufacturer = this.getManufacturerFromParam(manufacturerValue);
    if (manufacturer) {
      return manufacturer.slug || manufacturer.name || manufacturerValue;
    }

    const option = filter?.options?.find(
      (filterOption) => filterOption.value === manufacturerValue
    );
    if (option?.name) {
      return option.name;
    }

    return manufacturerValue;
  };

  getManufacturerFilterValuesFromFilters = (filters) => {
    const values = get(filters, MANUFACTURER_FILTER_KEY);
    if (Array.isArray(values)) {
      return values;
    }
    if (values) {
      return [values];
    }
    return [];
  };

  getManufacturerFilterValues = (searchParameters) =>
    this.getManufacturerFilterValuesFromFilters(searchParameters.filters || {});

  getManufacturerRoutingState = (searchParameters) => {
    const manufacturerParam = this.getManufacturerParam();
    const selectedManufacturers =
      this.getManufacturerFilterValues(searchParameters);
    const hasOtherFilters = this.hasNonManufacturerFilters(
      searchParameters.filters
    );
    const hasSort = this.hasSortParams(searchParameters);
    const hasOtherParams = this.hasOtherQueryParams(searchParameters);
    const isPageOne = this.isPageOne(searchParameters);
    const hasFilters =
      searchParameters.filters &&
      Object.keys(searchParameters.filters).length > 0;

    return {
      manufacturerParam,
      selectedManufacturers,
      hasOtherFilters,
      hasSort,
      hasOtherParams,
      isPageOne,
      hasFilters,
    };
  };

  shouldPromoteManufacturerToPath = (routingState) => {
    if (!this.hasCategoryContext()) {
      return false;
    }

    return (
      !routingState.manufacturerParam &&
      routingState.selectedManufacturers.length === 1 &&
      !routingState.hasOtherFilters &&
      !routingState.hasSort &&
      !routingState.hasOtherParams &&
      routingState.isPageOne
    );
  };

  shouldStripPageFromManufacturerPath = (routingState) => {
    if (!this.hasCategoryContext()) {
      return false;
    }

    return (
      routingState.manufacturerParam &&
      routingState.isPageOne &&
      !routingState.hasFilters &&
      !routingState.hasSort &&
      !routingState.hasOtherParams
    );
  };

  removeManufacturerFromFilters = (filters, valuesToRemove) => {
    const nextFilters = Array.isArray(filters)
      ? [...filters]
      : { ...(filters || {}) };
    const values = this.getManufacturerFilterValuesFromFilters(nextFilters);
    const remaining = values.filter((value) => !valuesToRemove.includes(value));
    if (remaining.length > 0) {
      setObject(nextFilters, MANUFACTURER_FILTER_KEY, remaining);
    } else {
      unset(nextFilters, MANUFACTURER_FILTER_KEY);
    }
    return nextFilters;
  };

  mergeManufacturerFilters = (
    urlManufacturerValue,
    manufacturerFilters,
    includeUrl
  ) => {
    const combined = [];
    if (includeUrl) {
      combined.push(urlManufacturerValue);
    }
    manufacturerFilters.forEach((value) => {
      if (!combined.includes(value)) {
        combined.push(value);
      }
    });
    return combined;
  };

  hasNonManufacturerFilters = (filters = {}) => {
    const { [MANUFACTURER_FILTER_KEY]: _, ...rest } = filters || {};
    return (
      Object.keys(rest).length > 0 &&
      Object.values(rest).some((value) => {
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        return value !== undefined && value !== null;
      })
    );
  };

  hasSortParams = (searchParameters) =>
    searchParameters.sort && Object.keys(searchParameters.sort).length > 0;

  hasOtherQueryParams = (searchParameters) =>
    Object.keys((({ filters, sort, page, ...rest }) => rest)(searchParameters))
      .length > 0;

  isPageOne = (searchParameters) =>
    !searchParameters.page ||
    searchParameters.page === 1 ||
    searchParameters.page === '1';

  buildSearchString = (searchParameters) =>
    stringify(
      MainProductList.transformParametersBeforeStringify(searchParameters)
    );

  buildSearchStringWithPageReset = (searchParameters, keepPage) => {
    const nextParameters = { ...searchParameters };
    if (keepPage) {
      nextParameters.page = 1;
    } else {
      unset(nextParameters, 'page');
    }
    return this.buildSearchString(nextParameters);
  };

  maybeNormalizeManufacturerParam = () => {
    const { history, location } = this.props;
    if (!this.hasCategoryContext()) {
      return false;
    }

    const searchParameters = this.getSearchParameters();
    const routingState = this.getManufacturerRoutingState(searchParameters);
    const categoryRouteState =
      this.getCategoryManufacturerRouteState(searchParameters);

    if (
      categoryRouteState.shouldRemoveManufacturerPath ||
      categoryRouteState.shouldStripDuplicateQueryManufacturers
    ) {
      const pathname = categoryRouteState.shouldKeepManufacturerPath
        ? location.pathname
        : this.buildCategoryPathWithManufacturer(null);
      const nextParameters = this.buildSearchParametersWithManufacturerValues(
        searchParameters,
        categoryRouteState.cleanedQueryManufacturerValues
      );

      history.replace({
        pathname,
        search: this.buildSearchString(nextParameters),
        hash: location.hash,
      });
      return true;
    }

    if (this.shouldStripPageFromManufacturerPath(routingState)) {
      history.replace({
        pathname: location.pathname,
        search: this.buildSearchStringWithPageReset(searchParameters, false),
        hash: location.hash,
      });
      return true;
    }

    if (!this.shouldPromoteManufacturerToPath(routingState)) {
      return false;
    }

    const pathname = this.buildCategoryPathWithManufacturer(
      routingState.selectedManufacturers[0]
    );
    const nextParameters = { ...searchParameters };
    unset(nextParameters, 'page');
    unset(nextParameters, 'filters');
    history.replace({
      pathname,
      search: this.buildSearchString(nextParameters),
      hash: location.hash,
    });
    return true;
  };

  getQueryIdentifier = () => {
    let productLoadParameters = this.getProductLoadParameters();

    productLoadParameters = this.addSearchTermToProductLoadParameters(
      productLoadParameters
    );
    return paramsToQueryIdentifier(productLoadParameters);
  };

  static propsToSearchParameters = (props) => {
    const {
      location: { search },
    } = props;

    const parsedSearchParameters = parse(search);

    return MainProductList.transformParameters(parsedSearchParameters);
  };

  setSearchParameters = (parameters = this.getSearchParameters()) => {
    const { history, location } = this.props;
    const sanitizedParameters = this.sanitizeSearchParameters(parameters);
    const routingState =
      sanitizedParameters &&
      this.getManufacturerRoutingState(sanitizedParameters);

    if (routingState && this.shouldPromoteManufacturerToPath(routingState)) {
      const pathname = this.buildCategoryPathWithManufacturer(
        routingState.selectedManufacturers[0]
      );
      const nextParameters = { ...sanitizedParameters };
      unset(nextParameters, 'page');
      unset(nextParameters, 'filters');
      history.push({
        pathname,
        search: this.buildSearchString(nextParameters),
        hash: location.hash,
      });
      return;
    }
    history.push({
      search: this.buildSearchString({
        ...sanitizedParameters,
        // Always reset page to 1 when filtering changes
        page: 1,
      }),
    });
  };

  sanitizeSearchParameters = (parameters) => {
    if (!this.hasCategoryContext()) {
      return parameters;
    }

    const routeState = this.getCategoryManufacturerRouteState(parameters);
    const manufacturerParam = routeState.effectiveManufacturerParam;
    if (!manufacturerParam) {
      return parameters;
    }

    const nextParameters = { ...parameters };
    if (nextParameters.filters && typeof nextParameters.filters === 'object') {
      nextParameters.filters = Array.isArray(nextParameters.filters)
        ? [...nextParameters.filters]
        : { ...nextParameters.filters };
      const manufacturerFilterValue = routeState.pathManufacturer
        ? String(routeState.pathManufacturer.id)
        : this.getManufacturerFilterValueFromParam(manufacturerParam);
      nextParameters.filters = this.removeManufacturerFromFilters(
        nextParameters.filters,
        [manufacturerParam, manufacturerFilterValue]
      );
    }

    return nextParameters;
  };

  /**
   * Transform "f "and "s" to "filter" and "sort".
   */
  static transformParameters(parameters) {
    parameters.filters = parameters.f || {};
    delete parameters.f;
    parameters.sort = parameters.s || {};
    delete parameters.s;

    return parameters;
  }

  /**
   * Transform "filter" and "sort" to "f" and "s".
   */
  static transformParametersBeforeStringify(parameters) {
    parameters.f = parameters.filters;
    delete parameters.filters;
    parameters.s = parameters.sort;
    delete parameters.sort;
    return parameters;
  }

  getProductLoadParameters = () => {
    const {
      fixedParams,
      configStore,
      showCategoryFilter,
      match,
      manufacturerStore,
    } = this.props;
    const searchParameters = this.getSearchParameters();

    const optimizer = {
      excludeContent: 1,
      optimizeResponse: 1,
    };
    const optimizedParams = { ...fixedParams, ...optimizer };

    if (!searchParameters) {
      return { ...optimizedParams };
    }

    if (
      showCategoryFilter &&
      this.categoryFilterActive(searchParameters) &&
      optimizedParams.cross_categories
    ) {
      delete optimizedParams.cross_categories;
    }

    const categoryRouteState =
      this.getCategoryManufacturerRouteState(searchParameters);
    const manufacturerFilterValue = categoryRouteState.pathManufacturer
      ? String(categoryRouteState.pathManufacturer.id)
      : this.getManufacturerParam();
    const hasValidManufacturer = Boolean(
      categoryRouteState.effectiveManufacturerParam && manufacturerFilterValue
    );
    const manufacturerFilters = categoryRouteState.shouldKeepManufacturerPath
      ? categoryRouteState.cleanedQueryManufacturerValues
      : this.getManufacturerFilterValues(searchParameters);
    const { [MANUFACTURER_FILTER_KEY]: _, ...restFilters } =
      searchParameters.filters || {};
    const combinedManufacturers = this.mergeManufacturerFilters(
      manufacturerFilterValue,
      manufacturerFilters,
      hasValidManufacturer
    );

    return {
      inStock: configStore.inStockProductsOnly ? '1' : '0',
      page: searchParameters.page,
      ...(combinedManufacturers.length > 0 && {
        [MANUFACTURER_FILTER_KEY]: combinedManufacturers,
      }),
      ...restFilters,
      ...optimizedParams,
      ...searchParameters.sort,
    };
  };

  categoryFilterActive = (searchParameters) => {
    return (
      searchParameters.filters.cross_categories &&
      searchParameters.filters.cross_categories.length > 0
    );
  };

  // TODO: Improve product list fetching "pipe".
  // Should main product list handle fetching products?
  loadProducts = () => {
    const { productStore, allSections, urlOverride, updateProductCount } =
      this.props;
    let productLoadParameters = this.getProductLoadParameters();
    productLoadParameters = this.addSearchTermToProductLoadParameters(
      productLoadParameters
    );
    const results = this.getCurrentSearchResult();

    if (!results && !this.getCurrentSearchState()) {
      productStore
        .loadProducts(productLoadParameters, allSections, urlOverride)
        .then(() => {
          const results = this.getCurrentSearchResult();
          if (results) {
            updateProductCount(results.total);
          }
        });
    } else if (results) {
      if (results.total > 0) {
        updateProductCount(results.total);
      }
    }
  };

  addSearchTermToProductLoadParameters = (productLoadParameters) => {
    if (
      !this.props.configStore.productList.filterProductsByText ||
      !this.state.searchTerm
    ) {
      return productLoadParameters;
    }

    productLoadParameters.text = this.state.searchTerm;
    return productLoadParameters;
  };

  onAcceptFilter = (filter, value) => {
    const searchParameters = cloneDeep(this.getSearchParameters());
    const { history, location } = this.props;
    const manufacturerParam = this.getManufacturerParam();

    if (filter.key === MANUFACTURER_FILTER_KEY && this.hasCategoryContext()) {
      const selectedManufacturers = Array.isArray(value)
        ? value
        : value
        ? [value]
        : [];
      let selectedManufacturer = selectedManufacturers[0];
      let remainingManufacturers = selectedManufacturers.slice(1);
      if (filter.type === FilterType.SINGLETERM) {
        selectedManufacturer =
          selectedManufacturers[selectedManufacturers.length - 1];
        remainingManufacturers = [];
      }

      if (selectedManufacturers.length > 1) {
        setObject(
          searchParameters.filters,
          MANUFACTURER_FILTER_KEY,
          selectedManufacturers
        );

        history.push({
          pathname: this.buildCategoryPathWithManufacturer(null),
          search: this.buildSearchStringWithPageReset(searchParameters, true),
          hash: location.hash,
        });
        return;
      }

      if (selectedManufacturer && remainingManufacturers.length > 0) {
        setObject(
          searchParameters.filters,
          MANUFACTURER_FILTER_KEY,
          remainingManufacturers
        );
      } else {
        unset(searchParameters.filters, MANUFACTURER_FILTER_KEY);
      }

      const pathname = this.buildCategoryPathWithManufacturer(
        selectedManufacturer,
        filter
      );

      const sanitizedParameters =
        selectedManufacturer || manufacturerParam
          ? this.sanitizeSearchParameters(searchParameters)
          : searchParameters;

      history.push({
        pathname,
        search: this.buildSearchStringWithPageReset(
          sanitizedParameters,
          remainingManufacturers.length > 0
        ),
        hash: location.hash,
      });
      return;
    }

    switch (filter.type) {
      case FilterType.BOOLEAN: {
        if (value) {
          setObject(searchParameters.filters, filter.key, 1);
        } else {
          unset(searchParameters.filters, filter.key);
        }
        break;
      }
      case FilterType.RANGE: {
        setObject(searchParameters.filters, filter.key, {
          min: Number(value[0]),
          max: Number(value[1]),
        });
        break;
      }
      case FilterType.SINGLETERM:
      case FilterType.TERM: {
        setObject(searchParameters.filters, filter.key, value);
        break;
      }
      default: {
        console.error('Unknown filter type ' + filter.type);
      }
    }

    this.setSearchParameters(searchParameters);
  };

  onInStockAccept = (filter, inStockFilterValue) => {
    const { configStore } = this.props;
    if (configStore.inStockProductsOnly !== inStockFilterValue) {
      configStore.setInStockProductsOnly(inStockFilterValue);
    }
    this.loadProducts();
  };

  removeFilter = (activeFilter, valueToRemove) => {
    const { history, location } = this.props;
    const manufacturerParam = this.getManufacturerParam();
    const manufacturerFilterValue =
      this.getManufacturerFilterValueFromParam(manufacturerParam);
    if (
      activeFilter.key === MANUFACTURER_FILTER_KEY &&
      manufacturerParam &&
      (valueToRemove === manufacturerParam ||
        valueToRemove === manufacturerFilterValue) &&
      this.hasCategoryContext()
    ) {
      const searchParameters = cloneDeep(this.getSearchParameters());
      searchParameters.filters = this.removeManufacturerFromFilters(
        searchParameters.filters,
        [valueToRemove, manufacturerFilterValue]
      );
      const remainingManufacturers =
        this.getManufacturerFilterValues(searchParameters);
      const categoryPath = this.buildCategoryPathWithManufacturer(null);
      history.push({
        pathname: categoryPath,
        search: this.buildSearchStringWithPageReset(
          searchParameters,
          remainingManufacturers.length > 0
        ),
        hash: location.hash,
      });
      return;
    }

    const searchParameters = cloneDeep(this.getSearchParameters());

    switch (activeFilter.filter.type) {
      case FilterType.SINGLETERM:
      case FilterType.TERM: {
        let values = get(searchParameters.filters, activeFilter.key);
        if (!Array.isArray(values)) {
          values = [];
        }
        let indexToRemove = values.indexOf(valueToRemove);
        if (indexToRemove !== -1) values.splice(indexToRemove, 1);
        break;
      }
      case FilterType.RANGE: {
        unset(searchParameters.filters, activeFilter.key + '[min]');
        unset(searchParameters.filters, activeFilter.key + '[max]');
        break;
      }
      default: {
        unset(searchParameters.filters, activeFilter.key);
      }
    }

    this.setSearchParameters(searchParameters);
  };

  clearFilters = () => {
    const { history, location } = this.props;
    const searchParameters = {
      ...this.getSearchParameters(),
      filters: [],
    };

    if (this.getManufacturerParam() && this.hasCategoryContext()) {
      const categoryPath = this.buildCategoryPathWithManufacturer(null);
      history.push({
        pathname: categoryPath,
        search: this.buildSearchStringWithPageReset(searchParameters, false),
        hash: location.hash,
      });
      return;
    }

    this.setSearchParameters(searchParameters);
  };

  onSelectSortOrder = (sortOrderKey, ascDesc) => {
    const searchParameters = cloneDeep(this.getSearchParameters());
    if (sortOrderKey) {
      set(searchParameters.sort, ProductSortParameters.ORDER_BY, sortOrderKey);
    } else {
      unset(searchParameters.sort, ProductSortParameters.ORDER_BY);
    }

    if (ascDesc) {
      set(searchParameters.sort, ProductSortParameters.ORDER, ascDesc);
    } else {
      unset(searchParameters.sort, ProductSortParameters.ORDER);
    }

    this.setSearchParameters(searchParameters);
  };

  getSelectedSortOrder = () => {
    const searchParameters = this.getSearchParameters();
    return get(searchParameters.sort, ProductSortParameters.ORDER);
  };

  renderProductListFilter = () => {
    const { disableQuickSearch } = this.props;
    if (disableQuickSearch) {
      return null;
    }

    return (
      <Row>
        <Col
          lg={8}
          xl={6}
          className="MainProductList__active-filter--quicksearch mr-lg-auto"
        >
          <ProductListFilter
            onProductListFiltering={this.filterProductList}
            clearProductListFilter={this.clearProductListFilter}
            searchTerm={this.state.searchTerm}
          />
        </Col>
      </Row>
    );
  };

  filterProductList = (searchTerm) => {
    if (searchTerm) {
      this.setSearchParameters();
      this.setState({ searchTerm });
    }

    if (!searchTerm && this.state.searchTerm) {
      this.clearProductListFilter();
    }
  };

  clearProductListFilter = () => {
    if (this.state.searchTerm) {
      this.setState({ searchTerm: '' });
    }
  };

  getSEOPagination = () => {
    const { location } = this.props;
    const { last_page: lastPage, current_page: currentPage } =
      this.getCurrentSearchResult();

    return (
      <SEOPagination
        currentPage={currentPage}
        lastPage={lastPage}
        path={location.pathname}
      />
    );
  };

  getPaginator = () => {
    const { uiStore, location } = this.props;
    const { last_page: lastPage, current_page: currentPage } =
      this.getCurrentSearchResult();

    if (lastPage < 2) {
      return null;
    }

    let searchTemplate;
    const regex = /page=(\d+?)(?=(&|$))/;
    const pageParameter = `page=:page`;

    const urlEncodedParameters = escapeUrlParametersForPaginatorTemplate(
      location.search
    );

    if (location.search.charAt(0) !== '?') {
      searchTemplate = location.search + '?' + pageParameter;
    } else {
      searchTemplate = urlEncodedParameters.match(regex)
        ? urlEncodedParameters.replace(regex, pageParameter)
        : urlEncodedParameters + '&' + pageParameter;
    }
    const pathTemplate =
      escapeUrlForPaginatorTemplate(location.pathname) + searchTemplate;

    if (uiStore.isMobile) {
      return (
        <MobilePaginator
          pathTemplate={pathTemplate}
          currentPage={currentPage}
          onClick={this.scrollToTop}
          lastPage={lastPage}
        />
      );
    }

    return (
      <Paginator
        pathTemplate={pathTemplate}
        currentPage={currentPage}
        lastPage={lastPage}
        pagesToShow={5}
        onClick={this.scrollToTop}
      />
    );
  };

  scrollToTop = () => {
    scrollToElementById(ANCHOR_ID, { behavior: 'instant' });
  };

  toggleShowAll = () => {
    this.setState({ showAllFilters: !this.state.showAllFilters });
  };

  renderAdditionalContentRobotsMeta = () => {
    const { additionalContent } = this.props;
    const productSearchResults = this.getCurrentSearchResult();
    return (
      !productSearchResults.isIndexableByRobots && (
        <additionalContent.robotsMeta noindex nofollow />
      )
    );
  };

  getSelectedSortOrderBy = () => {
    const searchParameters = this.getSearchParameters();
    return get(searchParameters.sort, ProductSortParameters.ORDER_BY);
  };

  redirectToProduct = (product) => {
    const { routeService, location } = this.props;
    const queryParams = parse(location.search);
    const focusQuantity = queryParams.focusQuantity;

    const path =
      product.class === ProductClass.MULTI_CHILD
        ? product.pathWithActiveProductId(product.id)
        : product.path;

    const pathToNavigate = routeService.getProductPath(product, path);

    let to = {
      pathname: pathToNavigate,
    };

    if (focusQuantity) {
      to = {
        ...to,
        search: `?focusQuantity=${focusQuantity}`,
      };
    }

    return <Redirect to={to} />;
  };

  getActiveFilters = () => {
    const searchResult = this.getCurrentSearchResult();
    if (!searchResult) {
      return [];
    }
    const searchParameters = cloneDeep(this.getSearchParameters());
    const availableFilters = searchResult.available_filters || [];
    const activeFilters = availableFilters.reduce((activeFilters, filter) => {
      if (has(searchParameters.filters, filter.key)) {
        const filterValue = get(searchParameters.filters, filter.key);
        activeFilters.push({
          key: filter.key,
          filter,
          name: filter.name,
          value: filterValue.min || filterValue.max ? null : filterValue,
          min: filterValue.min ? Number(filterValue.min) : null,
          max: filterValue.max ? Number(filterValue.max) : null,
        });
      }
      return activeFilters;
    }, []);
    const categoryRouteState =
      this.getCategoryManufacturerRouteState(searchParameters);
    const manufacturerParam = categoryRouteState.effectiveManufacturerParam;
    if (manufacturerParam) {
      const manufacturerActiveFilter = activeFilters.find(
        (activeFilter) => activeFilter.key === MANUFACTURER_FILTER_KEY
      );
      const manufacturerFilterValue = categoryRouteState.pathManufacturer
        ? String(categoryRouteState.pathManufacturer.id)
        : this.getManufacturerFilterValueFromParam(manufacturerParam);
      if (manufacturerActiveFilter) {
        const currentValue = manufacturerActiveFilter.value;
        const values = Array.isArray(currentValue)
          ? [...currentValue]
          : currentValue
          ? [currentValue]
          : [];
        if (!values.includes(manufacturerFilterValue)) {
          values.unshift(manufacturerFilterValue);
          manufacturerActiveFilter.value = values;
        }
      } else {
        const manufacturerFilter = availableFilters.find(
          (filter) => filter.key === MANUFACTURER_FILTER_KEY
        );
        const fallbackFilter = {
          key: MANUFACTURER_FILTER_KEY,
          name: 'Manufacturer',
          type: FilterType.TERM,
          options: [
            {
              value: manufacturerParam,
              name: manufacturerParam,
            },
          ],
        };
        const filterToUse = manufacturerFilter || fallbackFilter;
        activeFilters.push({
          key: MANUFACTURER_FILTER_KEY,
          filter: filterToUse,
          name: filterToUse.name,
          value: [manufacturerFilterValue],
          min: null,
          max: null,
        });
      }
    }
    return activeFilters;
  };

  getCurrentSearchResult = () =>
    this.props.productStore.productQueryResults.get(this.getQueryIdentifier());

  getCurrentSearchState = () =>
    this.props.productStore.productQueryStates.get(this.getQueryIdentifier());

  render() {
    const {
      intl,
      configStore,
      productStore,
      fixedParams,
      listId,
      name,
      showSort,
      cardSettings,
      redirectOnSingleMatch,
      showCategoryFilter,
      productParams,
      showCategoryHeaders,
      advancedSearchPage,
      additionalContent,
      productListRef,
    } = this.props;

    const { showAllFilters } = this.state;
    if (!productStore || !fixedParams) {
      return;
    }

    return (
      <div className="MainProductList" ref={productListRef}>
        <ScrollableAnchor id={ANCHOR_ID} />
        <ContentForState
          state={this.getCurrentSearchState()}
          forPlaceHolder={<PageSkeleton />}
          forLoaded={() => {
            const searchResult = this.getCurrentSearchResult();
            const products = searchResult.data;
            const hasProducts = products && products.length > 0;

            const filters =
              searchResult && searchResult.available_filters
                ? searchResult.available_filters.filter((filter) => {
                    if (filter.key === 'cross_categories') {
                      return showCategoryFilter;
                    }
                    return !has(fixedParams, filter.key);
                  })
                : [];
            const countOfProducts = searchResult ? searchResult.total : 0;
            const activeFilters = this.getActiveFilters();
            const hasManufacturerParam =
              this.props.isCategoryPage &&
              this.props.match?.params?.manufacturer;
            const hasAdditionalActiveFilters =
              activeFilters.length > (hasManufacturerParam ? 1 : 0);

            const shouldRedirectToProduct =
              redirectOnSingleMatch &&
              products.length === 1 &&
              countOfProducts === 1 &&
              activeFilters.length === 0;

            if (shouldRedirectToProduct) {
              return this.redirectToProduct(head(products));
            }

            // In stock filter is shown separately
            const [inStockFilters, restOfFilters] = partition(filters, [
              'key',
              IN_STOCK_FILTER_KEY,
            ]);
            const inStockFilter = head(inStockFilters);
            const restOfActiveFilters = activeFilters.filter(
              (activeFilter) => activeFilter.key !== IN_STOCK_FILTER_KEY
            );

            const doShowHeaders =
              showCategoryHeaders ||
              this.getSelectedSortOrderBy() === ProductSortOrderBy.CATEGORY ||
              (!this.getSelectedSortOrderBy() &&
                !advancedSearchPage &&
                configStore.productList.defaultSortOption ===
                  ProductSortOrderBy.CATEGORY);

            const showQuickSearch =
              configStore.productList.filterProductsByText &&
              (hasProducts || this.state.searchTerm);
            const showBooleanFilter =
              !configStore.hideProductWithZeroQuantity &&
              configStore.siteConfig.isWebStore;
            const showActiveFilterRow = !configStore.siteConfig.isHomePage;
            const hasVisibleFilterControls =
              (hasProducts && restOfFilters.length > 0) ||
              restOfActiveFilters.length > 0 ||
              showQuickSearch ||
              showBooleanFilter ||
              showActiveFilterRow;

            return (
              <>
                {additionalContent?.robotsMeta &&
                  this.renderAdditionalContentRobotsMeta()}
                {hasAdditionalActiveFilters ? (
                  <RobotsMeta noindex />
                ) : (
                  this.getSEOPagination()
                )}
                <section
                  className="MainProductList__product-list MainProductList__content"
                  aria-label={intl.formatMessage(
                    globalTranslations.productsTitle
                  )}
                >
                  {hasProducts ? (
                    <ProductList
                      products={products}
                      listId={listId}
                      name={name}
                      cardSettings={cardSettings}
                      productParams={productParams}
                      showCategoryHeaders={doShowHeaders}
                    />
                  ) : (
                    <NoProductsFound />
                  )}
                  {hasProducts && this.getPaginator()}
                </section>
                {hasVisibleFilterControls && (
                  <aside
                    className="MainProductList__filters"
                    aria-label={intl.formatMessage(
                      globalTranslations.productFiltersAndControlsRegionLabel
                    )}
                  >
                    {hasProducts && (
                      <FilteringOptions
                        filters={restOfFilters}
                        activeFilters={restOfActiveFilters}
                        onAcceptFilter={this.onAcceptFilter}
                        toggleShowAll={this.toggleShowAll}
                        showAllFilters={showAllFilters}
                        inGrid
                      />
                    )}
                    <ActiveFilterList
                      activeFilters={restOfActiveFilters}
                      removeFilter={this.removeFilter}
                      clearFilters={this.clearFilters}
                      horizontal
                    />
                    {showQuickSearch && this.renderProductListFilter()}
                    {showActiveFilterRow && (
                      <div className="MainProductList__active-filter-row">
                        <ProductListTools
                          countOfProducts={countOfProducts}
                          onSelectSort={this.onSelectSortOrder}
                          selectedSortOrderBy={this.getSelectedSortOrderBy()}
                          selectedSortOrder={this.getSelectedSortOrder()}
                          showSort={showSort}
                        />
                        {showBooleanFilter && (
                          <BooleanFilter
                            onAccept={this.onInStockAccept}
                            filter={inStockFilter}
                            isSelected={configStore.inStockProductsOnly}
                          />
                        )}
                      </div>
                    )}
                  </aside>
                )}
              </>
            );
          }}
        />
      </div>
    );
  }
}

MainProductList.propTypes = {
  categoryStore: modelOf(CategoryStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  manufacturerStore: modelOf(ManufacturerStore).isRequired,
  productStore: modelOf(ProductStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  history: RouterPropTypes.history.isRequired,
  location: RouterPropTypes.location.isRequired,
  match: RouterPropTypes.match.isRequired,
  fixedParams: PropTypes.object.isRequired,
  listId: PropTypes.string,
  name: PropTypes.string,
  searchTerm: PropTypes.string,
  urlOverride: PropTypes.string,
  advancedSearchPage: PropTypes.bool,
  allSections: PropTypes.bool,
  disableQuickSearch: PropTypes.bool,
  redirectOnSingleMatch: PropTypes.bool,
  showCategoryFilter: PropTypes.bool,
  showCategoryHeaders: PropTypes.bool,
  showSort: PropTypes.bool,
  isCategoryPage: PropTypes.bool,
  additionalContent: PropTypes.object,
  dynamicParams: PropTypes.object,
  productListRef: PropTypes.object,
  clearProductListFilter: PropTypes.func,
  productListFilterTerm: PropTypes.func,
  updateProductCount: PropTypes.func,
  intl: intlShape.isRequired,
};

MainProductList.defaultProps = {
  showSort: true,
  allSections: false,
  redirectOnSingleMatch: false,
  urlOverride: null,
  disableQuickSearch: false,
  showCategoryHeaders: false,
  advancedSearchPage: false,
  isCategoryPage: false,
  updateProductCount: () => null,
};

export default withRouter(
  injectIntl(
    inject(
      'categoryStore',
      'configStore',
      'manufacturerStore',
      'productStore',
      'routeService',
      'uiStore'
    )(MainProductList)
  )
);
